import React, { useEffect, useState } from "react";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import axiosInstance from "../../utils/axios";
import uploadToCloudinary from "../../utils/cloudinary";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { FiInfo } from "react-icons/fi";
import {
  FaSpinner,
  FaImage,
  FaTrash,
  FaGripVertical,
  FaEdit,
} from "react-icons/fa";
import { useDragReorder } from "../../hooks/useDragReorder";

export const NoStatsView = ({ onAdd }) => (
  <div className="p-8 border border-dashed border-gray-300 rounded-lg bg-white text-center">
    <h3 className="text-gray-700 font-medium mb-2">No stats found</h3>
    <p className="text-gray-500 text-sm mb-4">
      You haven't created any impact stats yet.
    </p>
    <button
      onClick={onAdd}
      className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90"
    >
      + Add Stat
    </button>
  </div>
);

export const StatCardSkeleton = () => (
  <div className="p-3 bg-white rounded-lg shadow-sm border animate-pulse">
    {/* Top row skeleton */}
    <div className="flex justify-between items-start mb-2">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-200 rounded" />
        <div className="w-12 h-3 bg-gray-200 rounded" />
      </div>

      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-gray-200 rounded" />
        <div className="w-8 h-8 bg-gray-200 rounded" />
      </div>
    </div>

    {/* Circle skeleton */}
    <div className="flex justify-center py-5">
      <div className="w-32 h-32 bg-gray-200 rounded-full" />
    </div>

    {/* Status button skeleton */}
    <div className="mt-3 flex justify-center">
      <div className="w-20 h-5 bg-gray-200 rounded" />
    </div>
  </div>
);

/* IMAGE UPLOAD MODAL */
const ImageUploadModal = ({ initialBg, onClose, onSave }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initialBg || "");
  const [uploading, setUploading] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!file) return toast.error("Pick an image first");
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      onSave(url);
      toast.success("Image uploaded");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6 relative">
        <h3 className="text-lg font-semibold mb-3 text-primaryTextColor">
          Change Background Image
        </h3>

        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-2">
            Recommended size: <strong>450 x 450</strong>. Square images give
            best results.
          </div>

          <div className="w-full h-48 rounded-md border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
            {preview ? (
              <img
                src={preview}
                alt="preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-gray-400">No preview</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="px-3 py-2 bg-gray-100 border rounded cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
            />
            <div className="flex items-center gap-2">
              <FaImage /> Choose Image
            </div>
          </label>

          <button
            onClick={handleUpload}
            className="px-4 py-2 bg-primaryColor text-white rounded hover:opacity-90 disabled:opacity-60"
            disabled={uploading || !file}
          >
            {uploading ? (
              <span className="inline-flex items-center gap-2">
                <FaSpinner className="animate-spin" /> Uploading
              </span>
            ) : (
              "Upload & Save"
            )}
          </button>

          <button onClick={onClose} className="px-3 py-2 border rounded">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const StatCirclePreview = ({ value, suffix, label, bg }) => {
  return (
    <div className="flex flex-col items-center justify-center group w-full max-w-xs mx-auto p-2 sm:p-3 md:p-4">
      <div className="relative flex flex-col items-center justify-center aspect-square w-full max-w-[8rem] sm:max-w-[10rem] md:max-w-[12rem] lg:max-w-[11rem] xl:max-w-[13rem] rounded-full border-2 border-white shadow-lg bg-transparent backdrop-blur-sm overflow-hidden transition-all duration-300 ">
        {/* Background image overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat "
          style={{ backgroundImage: `url(${bg})` }}
        />
        <div className="absolute inset-0 bg-black/20 " />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-2 sm:px-3 md:px-4">
          <span
            className="font-bold text-white drop-shadow-lg transition-all duration-300 group-hover:scale-110 text-center leading-none"
            style={{ fontSize: "clamp(1.25rem, 4vw, 2.5rem)" }}
          >
            {value}
            {suffix}
          </span>
          <p
            className="font-medium md:font-semibold text-center text-[#FCC101] uppercase tracking-wide leading-tight mt-1 sm:mt-2 px-1 transition-all duration-300 group-hover:text-yellow-300 break-words hyphens-auto"
            style={{ fontSize: "clamp(0.6rem, 1.8vw, 0.85rem)" }}
          >
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ADD / EDIT MODAL */
const AddEditModal = ({ initialData = null, onClose, onSaved }) => {
  const [form, setForm] = useState({
    label: "",
    value: 0,
    suffix: "+",
    bg: "",
    sequence: 0,
    isActive: true,
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        label: initialData.label || "",
        value: initialData.value ?? 0,
        suffix: initialData.suffix ?? "+",
        bg: initialData.bg || "",
        sequence: initialData.sequence ?? 0,
        isActive:
          typeof initialData.isActive === "boolean"
            ? initialData.isActive
            : true,
        _id: initialData._id,
      });
      setPreview(initialData.bg || "");
    } else {
      setForm((p) => ({ ...p, sequence: 0, isActive: true }));
      setPreview("");
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "value" || name === "sequence") {
      setForm((p) => ({ ...p, [name]: value === "" ? "" : Number(value) }));
    } else if (name === "isActive") {
      setForm((p) => ({ ...p, isActive: value === "true" }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUploadAndGetUrl = async () => {
    if (!file) return form.bg;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      return url;
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label?.trim()) return toast.error("Label is required");
    setSaving(true);
    try {
      const bgUrl = await handleUploadAndGetUrl();
      const payload = {
        label: form.label.trim(),
        value: Number(form.value) || 0,
        suffix: form.suffix || "",
        bg: bgUrl || form.bg || "",
        sequence: Number(form.sequence) || 0,
        isActive: !!form.isActive,
      };

      let resp;
      if (form._id) {
        resp = await axiosInstance.put(`/impact-stats/${form._id}`, payload);
      } else {
        resp = await axiosInstance.post(`/impact-stats`, payload);
      }
      const createdOrUpdated = resp?.data?.data ?? resp?.data;
      toast.success(form._id ? "Updated" : "Created");
      onSaved(createdOrUpdated);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6 relative">
        <h3 className="text-lg font-semibold mb-3 text-primaryTextColor">
          {form._id ? "Edit Stat" : "Add New Stat"}
        </h3>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <label className="block text-sm font-medium mb-1">Label *</label>
            <input
              name="label"
              value={form.label}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Value *</label>
            <input
              name="value"
              type="number"
              min="0"
              value={form.value}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Suffix</label>
            <input
              name="suffix"
              value={form.suffix}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
              placeholder="+, K, M, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Sequence</label>
            <input
              name="sequence"
              type="number"
              min="0"
              value={form.sequence}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">
              Background Image
            </label>
            <div className="flex items-center gap-3">
              <label className="w-28 h-28 relative rounded-md border-dashed overflow-hidden border border-gray-400 flex items-center justify-center cursor-pointer">
                {preview ? (
                  <img
                    src={preview}
                    alt="preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-gray-400">Click to upload</div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </label>

              <div className="text-sm text-gray-600">
                Recommended: <strong>450 x 450</strong>. Square images look
                best.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="block text-sm font-medium">Status</label>
            <select
              name="isActive"
              value={String(form.isActive)}
              onChange={(e) =>
                handleChange({
                  target: { name: "isActive", value: e.target.value },
                })
              }
              className="border px-3 py-2 rounded"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-4 py-2 rounded bg-primaryColor text-white disabled:opacity-60"
              disabled={saving}
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <FaSpinner className="animate-spin" /> Saving
                </span>
              ) : form._id ? (
                "Save Changes"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminImpactStats = () => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [imageModalItem, setImageModalItem] = useState(null);
  const {
    list: items,
    orderChanged,
    initialize,
    onDragStart,
    onDragOver,
    onDrop,
    cancelOrder,
    saveOrder,
    setList: setItems,
  } = useDragReorder();

  const fetchItems = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get("/impact-stats/admin");
      const data = resp?.data?.data ?? resp?.data ?? [];
      const sorted = Array.isArray(data)
        ? data.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        : [];
      initialize(sorted);
    } catch (err) {
      console.error("Fetch impact stats error:", err);
      toast.error("Failed to load impact stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddClick = () => {
    setEditItem(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setIsModalOpen(true);
  };

  const handleSaved = (saved) => {
    setItems((prev) => {
      const found = prev.find((p) => p._id === (saved._id || saved.id));
      if (found) {
        return prev
          .map((p) => (p._id === (saved._id || saved.id) ? saved : p))
          .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      }
      return [...prev, saved].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      );
    });
  };
  const saveToServer = async (updatedList) => {
    await Promise.all(
      updatedList.map((item) =>
        axiosInstance.put(`/impact-stats/${item._id}`, {
          sequence: item.sequence,
        })
      )
    );
    toast.success("Order updated");
  };

  const handleSave = () => saveOrder(saveToServer);

  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${item.label}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/impact-stats/${item._id}`);
          setItems((p) => p.filter((x) => x._id !== item._id));
          toast.success("Deleted");
        } catch (err) {
          console.error(err);
          toast.error("Delete failed");
        }
      }
    });
  };

  const handleToggleStatus = async (item) => {
    try {
      const resp = await axiosInstance.put(`/impact-stats/${item._id}`, {
        isActive: !item.isActive,
      });
      const updated = resp?.data?.data ?? resp?.data;
      setItems((prev) => prev.map((p) => (p._id === item._id ? updated : p)));
      toast.success("Status updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  const openImageModal = (item) => {
    setImageModalItem(item);
  };

  const handleImageSaved = async (newUrl) => {
    if (!imageModalItem) return;
    try {
      const resp = await axiosInstance.put(
        `/impact-stats/${imageModalItem._id}`,
        { bg: newUrl }
      );
      const updated = resp?.data?.data ?? resp?.data;
      setItems((prev) =>
        prev.map((p) => (p._id === imageModalItem._id ? updated : p))
      );
      toast.success("Background updated");
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setImageModalItem(null);
    }
  };

  const handleSearch = (q) => {
    if (!q) {
      fetchItems();
      return;
    }
    const lower = q.toLowerCase();
    setItems((prev) =>
      prev.filter((it) => (it.label || "").toLowerCase().includes(lower))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSearchBar
        title="Interventions Stats Management"
        onAddNewClick={handleAddClick}
        onSearchClick={handleSearch}
        hideAddButton={false}
      />
      {orderChanged && (
        <div className="px-6 py-3 bg-yellow-100 border-b border-yellow-300 flex items-center justify-between">
          <div className="flex items-center gap-2 text-yellow-800">
            <FiInfo />
            <span>Order changed. Save changes?</span>
          </div>
          <div className="flex gap-3">
            <button
              className="px-4 py-1 bg-primaryColor text-white rounded"
              onClick={handleSave}
            >
              Save Order
            </button>
            <button className="px-4 py-1 border rounded" onClick={cancelOrder}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="px-6 pb-6">
        <div className="my-2 flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded">
          <FiInfo className="text-primaryColor min-w-4 min-h-4" />
          <span>
            Drag cards to adjust their order. Click <strong>Edit</strong> to
            update labels, numbers, suffix, and background.
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-6">
            <NoStatsView onAdd={handleAddClick} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            <AnimatePresence>
              {items.map((item, idx) => (
                <motion.div
                  key={item._id}
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, idx)}
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                  className="p-3 bg-white rounded-lg shadow-sm border"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
                      <FaGripVertical />
                      <span className="text-xs">Seq {item.sequence}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditClick(item)}
                        className="p-2 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>

                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 rounded hover:bg-red-50 text-red-600"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col items-center">
                    <StatCirclePreview
                      value={item.value}
                      suffix={item.suffix}
                      label={item.label}
                      bg={item.bg}
                      onOpenImage={() => openImageModal(item)}
                    />

                    <div className="mt-3 w-full flex items-center justify-center">
                      <button
                        onClick={() => handleToggleStatus(item)}
                        className={`text-xs px-3 py-1 rounded ${
                          item.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {item.isActive ? "active" : "inactive"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddEditModal
          initialData={editItem}
          onClose={() => {
            setIsModalOpen(false);
            setEditItem(null);
          }}
          onSaved={handleSaved}
        />
      )}

      {imageModalItem && (
        <ImageUploadModal
          initialBg={imageModalItem.bg}
          onClose={() => setImageModalItem(null)}
          onSave={handleImageSaved}
        />
      )}
    </div>
  );
};

export default AdminImpactStats;
